import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Wrench, Package, Truck, Warehouse, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import StokSekme from '../../components/StokSekme';
import StokTablosu from '../../components/StokTablosu';
import StokAktarModal from '../../components/StokAktarModal';
import { formatCurrency } from '../../components/StockIndicator';
import DemirTable from './DemirTable';
import DemirModal from './DemirModal';
import StokModal from './StokModal';
import { demirCaplari, getDemirByInsaat } from '@mocks/materials/demir';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import { getHareketler, hareketTipleri } from '@mocks/materials/stokHareketleri';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Demir() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [activeTab, setActiveTab] = useState('kullanim');
    const [filterCap, setFilterCap] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isStokOpen, setIsStokOpen] = useState(false);
    const [isAktarOpen, setIsAktarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getDemirByInsaat(insaatId);
        if (filterCap) data = data.filter(item => item.cap === filterCap);
        return data;
    }, [insaatId, filterCap]);

    const stats = useMemo(() => {
        const toplamTonaj = filteredData.reduce((acc, s) => acc + s.miktar, 0);
        const teslimTonaj = filteredData.filter(s => s.durum === 'teslim').reduce((acc, s) => acc + s.miktar, 0);
        const stokTonaj = filteredData.filter(s => s.durum === 'teslim').reduce((acc, s) => acc + s.stokMiktar, 0);
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        return { toplamTonaj, teslimTonaj, stokTonaj, toplamMaliyet };
    }, [filteredData]);

    const stokHavuzuData = getStokHavuzu('demir');
    const hareketlerData = getHareketler('demir');

    // Handlers
    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };
    const handleUpdateStock = (item) => { setSelectedItem(item); setIsStokOpen(true); };

    const handleSubmit = async (formData, isEdit) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));

        // Cari hareket oluştur (firma seçildiyse)
        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            addMockCariHareket(formData.supplierId, formData.insaatId || insaatId || 'c1', {
                type: 'purchase',
                subtype: 'fatura',
                amount: formData.kdvDahilToplam,
                date: formData.siparisTarihi || new Date().toISOString().split('T')[0],
                description: `Demir Alımı — ${formData.miktar} ton ${formData.cap}`,
            });
        }

        toast.success(isEdit ? 'Demir siparişi güncellendi' : 'Yeni demir siparişi oluşturuldu');
        setIsLoading(false);
        setIsModalOpen(false);

        // Senaryo A: Ödeme planı mini modal
        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            setOdemePlanData({ amount: formData.kdvDahilToplam, companyName: formData.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };

    const handleOdemePlanConfirm = ({ nextDueDate, paymentSubtype }) => {
        toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`);
        setIsOdemePlanOpen(false);
        setOdemePlanData(null);
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success('Demir siparişi silindi');
        setIsLoading(false);
        setIsDeleteOpen(false);
    };

    const handleStokUpdate = async (stokData) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success(`Stok güncellendi: ${stokData.stokMiktar} ton`);
        setIsLoading(false);
        setIsStokOpen(false);
    };

    const handleAktarim = async (data) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success(`${data.miktar} ton stok aktarıldı`);
        setIsLoading(false);
        setIsAktarOpen(false);
    };

    const statsCards = [
        { title: 'Toplam Tonaj', value: `${stats.toplamTonaj} ton`, icon: Package, color: 'primary' },
        { title: 'Teslim Edilen', value: `${stats.teslimTonaj} ton`, icon: Truck, color: 'success' },
        { title: 'Stok Durumu', value: `${stats.stokTonaj} ton`, icon: Warehouse, color: 'info' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'warning' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'tedarikci', label: 'Tedarikçi' },
        { key: 'cap', label: 'Çap' },
        { key: 'kalite', label: 'Kalite' },
        { key: 'miktar', label: 'Toplam', type: 'number' },
        { key: 'stokMiktar', label: 'Stok', type: 'number' },
        { key: 'kullanilanMiktar', label: 'Kullanılan', type: 'number' },
        { key: 'toplamFiyat', label: 'Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' },
    ];

    const headerFilter = (
        <div className="flex items-center gap-3">
            <StokSekme activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'kullanim' && (
                <select
                    value={filterCap}
                    onChange={(e) => setFilterCap(e.target.value)}
                    className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
                >
                    <option value="">Tüm Çaplar</option>
                    {demirCaplari.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
            )}
        </div>
    );

    return (
        <>
            <MaterialPageLayout
                title="Demir"
                subtitle="Kaba İnşaat"
                icon={Wrench}
                iconBgColor="bg-info"
                backLink="/insaat/kaba"
                stats={statsCards}
                onAddClick={activeTab === 'kullanim' ? handleAdd : undefined}
                headerFilter={headerFilter}
            >
                {activeTab === 'kullanim' ? (
                    <DemirTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} onUpdateStock={handleUpdateStock} />
                ) : (
                    <StokTablosu
                        stokData={stokHavuzuData}
                        hareketData={hareketlerData}
                        hareketTipleri={hareketTipleri}
                        birim="ton"
                        onAktarim={() => setIsAktarOpen(true)}
                    />
                )}
            </MaterialPageLayout>

            <DemirModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Demir Siparişi Detayı" />
            <StokModal isOpen={isStokOpen} onClose={() => setIsStokOpen(false)} onSubmit={handleStokUpdate} data={selectedItem} isLoading={isLoading} />
            <StokAktarModal isOpen={isAktarOpen} onClose={() => setIsAktarOpen(false)} onSubmit={handleAktarim} malzemeTipi="demir" stokData={stokHavuzuData} birim="ton" isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="Siparişi Sil" message={`${selectedItem?.cap} çapında ${selectedItem?.miktar} ton demir siparişini silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal
                isOpen={isOdemePlanOpen}
                onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }}
                onConfirm={handleOdemePlanConfirm}
                amount={odemePlanData?.amount}
                companyName={odemePlanData?.companyName}
            />
        </>
    );
}
